import { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import { Analytics } from "@vercel/analytics/react"

function App() {
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const totalSecondsRef = useRef(0);
  const intervalRef = useRef(null);
  const wakeLockRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const htmlAudioRef = useRef(null);
  const htmlAudioPrimedRef = useRef(false);
  const startTimeRef = useRef(null);
  const hoursRef = useRef(hours);
  const minutesRef = useRef(minutes);
  const secondsRef = useRef(seconds);

  const buildBeepDataUrl = useCallback(() => {
    const sampleRate = 44100;
    const durationSeconds = 0.5;
    const numSamples = Math.floor(sampleRate * durationSeconds);
    const numChannels = 1;
    const bytesPerSample = 2;
    const dataSize = numSamples * numChannels * bytesPerSample;
    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);

    const writeAscii = (offset, text) => {
      for (let i = 0; i < text.length; i += 1) {
        view.setUint8(offset + i, text.charCodeAt(i));
      }
    };

    writeAscii(0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeAscii(8, 'WAVE');
    writeAscii(12, 'fmt ');
    view.setUint32(16, 16, true); // PCM chunk size
    view.setUint16(20, 1, true); // PCM format
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numChannels * bytesPerSample, true);
    view.setUint16(32, numChannels * bytesPerSample, true);
    view.setUint16(34, 16, true); // bits per sample
    writeAscii(36, 'data');
    view.setUint32(40, dataSize, true);

    const frequency = 880;
    let offset = 44;
    for (let i = 0; i < numSamples; i += 1) {
      const time = i / sampleRate;
      const envelope = Math.exp(-6 * time);
      const sample = Math.sin(2 * Math.PI * frequency * time) * envelope * 0.75;
      const pcm = Math.max(-1, Math.min(1, sample));
      view.setInt16(offset, pcm * 32767, true);
      offset += 2;
    }

    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.length; i += 1) {
      binary += String.fromCharCode(bytes[i]);
    }

    return `data:audio/wav;base64,${btoa(binary)}`;
  }, []);

  const ensureHtmlAudio = useCallback(() => {
    try {
      if (!htmlAudioRef.current) {
        const audio = new Audio(buildBeepDataUrl());
        audio.preload = 'auto';
        htmlAudioRef.current = audio;
      }
      return htmlAudioRef.current;
    } catch (error) {
      console.error('Error initializing HTML audio:', error);
      return null;
    }
  }, [buildBeepDataUrl]);

  const primeHtmlAudio = useCallback(async () => {
    const audio = ensureHtmlAudio();
    if (!audio || htmlAudioPrimedRef.current) return;

    try {
      audio.volume = 0.001;
      audio.currentTime = 0;
      await audio.play();
      audio.pause();
      audio.currentTime = 0;
      audio.volume = 1;
      htmlAudioPrimedRef.current = true;
    } catch (error) {
      console.error('Failed to prime HTML audio:', error);
    }
  }, [ensureHtmlAudio]);

  const playHtmlBeep = useCallback(async () => {
    const audio = ensureHtmlAudio();
    if (!audio) return false;

    try {
      audio.currentTime = 0;
      audio.volume = 1;
      await audio.play();
      return true;
    } catch (error) {
      console.error('HTML audio beep failed:', error);
      return false;
    }
  }, [ensureHtmlAudio]);

  const performBeep = useCallback((audioContext) => {
    try {
      const now = audioContext.currentTime;
      const startAt = now + 0.01;
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, startAt);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startAt + 0.5);

      oscillator.start(startAt);
      oscillator.stop(startAt + 0.5);
    } catch (error) {
      console.error('Error in performBeep:', error);
    }
  }, []);

  const ensureAudioContext = useCallback(async () => {
    try {
      if (!audioContextRef.current) {
        const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextCtor) return null;
        audioContextRef.current = new AudioContextCtor();
      }

      const audioContext = audioContextRef.current;

      if (audioContext.state !== 'running') {
        try {
          await audioContext.resume();
        } catch (error) {
          console.error('Failed to resume audio context:', error);
        }
      }

      if (!audioUnlockedRef.current) {
        try {
          const unlockOscillator = audioContext.createOscillator();
          const unlockGain = audioContext.createGain();
          unlockGain.gain.value = 0.0001;
          unlockOscillator.connect(unlockGain);
          unlockGain.connect(audioContext.destination);
          unlockOscillator.start();
          unlockOscillator.stop(audioContext.currentTime + 0.02);
          audioUnlockedRef.current = true;
        } catch (error) {
          console.error('Error with audio unlock:', error);
        }
      }

      return audioContext;
    } catch (error) {
      console.error('Error initializing audio context:', error);
      return null;
    }
  }, []);

  const playBeep = useCallback(async () => {
    try {
      if (await playHtmlBeep()) return;

      const audioContext = await ensureAudioContext();
      if (!audioContext || audioContext.state !== 'running') {
        console.error('Audio context not available/running');
        return;
      }
      performBeep(audioContext);
    } catch (error) {
      console.error('Error playing beep:', error);
    }
  }, [ensureAudioContext, performBeep, playHtmlBeep]);

  // Update ref when hours, minutes or seconds change (when not running)
  useEffect(() => {
    if (!isRunning) {
      totalSecondsRef.current = hours * 3600 + minutes * 60 + seconds;
    }
  }, [hours, minutes, seconds, isRunning]);

  useEffect(() => {
    hoursRef.current = hours;
    minutesRef.current = minutes;
    secondsRef.current = seconds;
  }, [hours, minutes, seconds]);

  // Countdown effect
  useEffect(() => {
    let interval;
    if (isRunning && totalSecondsRef.current > 0) {
      startTimeRef.current = Date.now();

      interval = setInterval(() => {
        const elapsedMs = Date.now() - startTimeRef.current;
        const elapsedSeconds = Math.floor(elapsedMs / 1000);
        const remainingSeconds = Math.max(0, totalSecondsRef.current - elapsedSeconds);

        const hrs = Math.floor(remainingSeconds / 3600);
        const mins = Math.floor((remainingSeconds % 3600) / 60);
        const secs = remainingSeconds % 60;

        setHours(hrs);
        setMinutes(mins);
        setSeconds(secs);

        if (remainingSeconds <= 0) {
          setIsRunning(false);
          void playBeep();
        }
      }, 500); // Check twice per second for better accuracy
    }

    return () => clearInterval(interval);
  }, [isRunning, playBeep]);

  useEffect(() => {
    if (!isRunning) {
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
      return;
    }

    let cancelled = false;

    const requestWakeLock = async () => {
      if (!('wakeLock' in navigator)) return;

      try {
        const wakeLock = await navigator.wakeLock.request('screen');
        if (cancelled) {
          wakeLock.release();
          return;
        }
        wakeLockRef.current = wakeLock;
      } catch (error) {
        console.error('Wake Lock request failed:', error);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isRunning) {
        requestWakeLock();
      }
    };

    requestWakeLock();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    };
  }, [isRunning]);

  const incrementHours = () => {
    if (!isRunning && hours < 23) {
      setHours(h => h + 1);
    }
  };

  const decrementHours = () => {
    if (!isRunning) {
      setHours(h => (h > 0 ? h - 1 : 0));
    }
  };

  const handlePointerDown = (event, action) => {
    event.preventDefault();
    action();
    intervalRef.current = setInterval(action, 200);
  };

  const handlePointerUp = () => {
    clearInterval(intervalRef.current);
  };

  const incrementMinutes = () => {
    if (!isRunning) {
      if (minutesRef.current === 59) {
        if (hoursRef.current < 23) {
          setHours(h => h + 1);
          setMinutes(0);
        }
        return;
      }
      setMinutes(m => m + 1);
    }
  };

  const decrementMinutes = () => {
    if (!isRunning) {
      if (minutesRef.current === 0) {
        if (hoursRef.current > 0) {
          setHours(h => h - 1);
          setMinutes(59);
        }
        return;
      }
      setMinutes(m => m - 1);
    }
  };

  const incrementSeconds = () => {
    if (!isRunning) {
      if (secondsRef.current === 59) {
        if (minutesRef.current === 59) {
          if (hoursRef.current < 23) {
            setHours(h => h + 1);
            setMinutes(0);
          }
          setSeconds(0);
          return;
        }
        setMinutes(m => m + 1);
        setSeconds(0);
        return;
      }
      setSeconds(s => s + 1);
    }
  };

  const decrementSeconds = () => {
    if (!isRunning) {
      if (secondsRef.current === 0) {
        if (minutesRef.current === 0) {
          if (hoursRef.current > 0) {
            setHours(h => h - 1);
            setMinutes(59);
            setSeconds(59);
          }
          return;
        }
        setMinutes(m => m - 1);
        setSeconds(59);
        return;
      }
      setSeconds(s => s - 1);
    }
  };

  const handleStart = () => {
    if (hours > 0 || minutes > 0 || seconds > 0) {
      void primeHtmlAudio();
      void ensureAudioContext();
      setIsRunning(true);
    }
  };

  const handleStop = () => {
    setIsRunning(false);
  };

  const handleReset = () => {
    setIsRunning(false);
    setHours(0);
    setMinutes(0);
    setSeconds(0);
  };

  return (
    <div className="App" data-darkmode={darkMode} style={{
      background: darkMode ? '#1a1a1a' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      minHeight: '100vh',
      padding: '20px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      boxSizing: 'border-box',
      overflow: 'hidden'
    }}>
      <div style={{ display: 'flex', gap: '20px', alignItems: 'center', justifyContent: 'center', padding: '20px', minHeight: '100vh', width: '100%', boxSizing: 'border-box' }}>
        
        {/* Test Sound Panel - Left */}
        <div style={{
          background: darkMode ? '#2a2a2a' : 'white',
          borderRadius: '20px',
          padding: '20px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          width: '140px',
          minWidth: '140px',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '15px',
          height: 'fit-content'
        }}>
          <button
            onClick={() => void playBeep()}
            style={{
              background: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '14px 20px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              width: '100%'
            }}
          >
            Test Sound
          </button>
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '10px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '8px' }}>
              <input
                type="checkbox"
                checked={darkMode}
                onChange={(e) => setDarkMode(e.target.checked)}
                style={{ display: 'none' }}
              />
              <div style={{
                width: '50px',
                height: '28px',
                background: darkMode ? '#22c55e' : '#ccc',
                borderRadius: '14px',
                position: 'relative',
                transition: 'background 0.3s ease',
                padding: '2px',
                boxSizing: 'border-box'
              }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  background: 'white',
                  borderRadius: '12px',
                  position: 'absolute',
                  left: darkMode ? '24px' : '2px',
                  top: '2px',
                  transition: 'left 0.3s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}></div>
              </div>
              <span style={{ fontSize: '12px', color: darkMode ? '#22c55e' : '#999', fontWeight: 'bold' }}>
                {darkMode ? 'dark mode' : 'dark mode'}
              </span>
            </label>
          </div>
        </div>

        {/* Timer Container - Center */}
        <div className="timer-container" style={{ minWidth: '600px', display: 'flex', flexDirection: 'column', background: darkMode ? '#2a2a2a' : 'white', color: darkMode ? '#fff' : '#333' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '20px' }}>
          <h1 style={{ margin: 0, color: darkMode ? '#fff' : '#333' }}>One Beep Timer</h1>
        </div>

        <div className="time-picker">
          <div className="time-input-group">
            <button
              onPointerDown={(event) => handlePointerDown(event, incrementHours)}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
              onPointerCancel={handlePointerUp}
              disabled={isRunning}
              className="arrow-btn up"
            >
              ▲
            </button>
            <div className="time-display">
              {String(hours).padStart(2, '0')}
            </div>
            <button
              onPointerDown={(event) => handlePointerDown(event, decrementHours)}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
              onPointerCancel={handlePointerUp}
              disabled={isRunning}
              className="arrow-btn down"
            >
              ▼
            </button>
            <div className="time-label">HRS</div>
          </div>

          <div className="time-separator">:</div>

          <div className="time-input-group">
            <button
              onPointerDown={(event) => handlePointerDown(event, incrementMinutes)}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
              onPointerCancel={handlePointerUp}
              disabled={isRunning}
              className="arrow-btn up"
            >
              ▲
            </button>
            <div className="time-display">
              {String(minutes).padStart(2, '0')}
            </div>
            <button
              onPointerDown={(event) => handlePointerDown(event, decrementMinutes)}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
              onPointerCancel={handlePointerUp}
              disabled={isRunning}
              className="arrow-btn down"
            >
              ▼
            </button>
            <div className="time-label">MIN</div>
          </div>

          <div className="time-separator">:</div>

          <div className="time-input-group">
            <button
              onPointerDown={(event) => handlePointerDown(event, incrementSeconds)}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
              onPointerCancel={handlePointerUp}
              disabled={isRunning}
              className="arrow-btn up"
            >
              ▲
            </button>
            <div className="time-display">
              {String(seconds).padStart(2, '0')}
            </div>
            <button
              onPointerDown={(event) => handlePointerDown(event, decrementSeconds)}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
              onPointerCancel={handlePointerUp}
              disabled={isRunning}
              className="arrow-btn down"
            >
              ▼
            </button>
            <div className="time-label">SEC</div>
          </div>
        </div>

        <div className="button-section">
          <button onClick={handleStart} disabled={isRunning || (hours === 0 && minutes === 0 && seconds === 0)}>
            Start
          </button>
          <button onClick={handleStop} disabled={!isRunning}>
            Stop
          </button>
          <button onClick={handleReset}>
            Reset
          </button>
        </div>
        </div>

        {/* Info Panel - Right */}
        <div style={{
          background: darkMode ? '#2a2a2a' : 'white',
          borderRadius: '20px',
          padding: '30px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          width: '350px',
          minWidth: '350px',
          flexShrink: 0,
          textAlign: 'left',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start'
        }}>
          <h2 style={{ color: '#667eea', fontSize: '24px', marginTop: 0, marginBottom: '20px' }}>
            Whatever for?
          </h2>
          <p style={{ color: darkMode ? '#ccc' : '#555', lineHeight: '1.6', margin: '15px 0', fontSize: '16px' }}>
            Hi! I created this website for people like me who have insomnia. Sometimes I tell myself 'Ok, I'll lay in bed for 20 minutes and if I don't fall asleep I'll get up and do stuff'.
          </p>
          <p style={{ color: darkMode ? '#ccc' : '#555', lineHeight: '1.6', margin: '15px 0', fontSize: '16px' }}>
            But I cannot set a normal timer on my phone, because it will wake me up even from my grave and I must get up to close it. I need something more gentle, that only lets me know once that the time has passed.
          </p>
          <p style={{ color: darkMode ? '#ccc' : '#555', lineHeight: '1.6', margin: '15px 0', fontSize: '16px' }}>
            And now you know. Of course, use this for anything you like. Have a good one!
          </p>
        </div>
      </div>

      <Analytics />
    </div>
  );
}

export default App;
